import { useId, useMemo, useState } from "react";
import { Button } from "@mui/material";

import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";

/**
 * "Exclude by pattern" (web_scraper_provider_design.md §10): a regular
 * expression over the page address, a live count over the loaded inventory,
 * and the choice to also save it to the crawl settings.
 */
export const ExcludeByPatternDialog = ({
    open,
    urls,
    totalLoaded,
    busy,
    handleClose,
    handleApply
}: {
    open: boolean;
    urls: string[];
    totalLoaded: number;
    busy: boolean;
    handleClose: () => void;
    handleApply: (pattern: string, persist: boolean) => void;
}) => {
    const id = useId();
    const [pattern, setPattern] = useState("");
    const [persist, setPersist] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    const compiled = useMemo(() => {
        if (!pattern.trim()) return null;
        try {
            return new RegExp(pattern.trim());
        } catch {
            return undefined;
        }
    }, [pattern]);
    const matches = compiled ? urls.filter(url => compiled.test(url)).length : 0;
    const error = !pattern.trim()
        ? "Enter a pattern."
        : compiled === undefined
          ? "The pattern is not a valid regular expression."
          : "";

    const submit = () => {
        if (error) {
            setShowErrors(true);
            return;
        }
        handleApply(pattern.trim(), persist);
    };

    return (
        <ModalTemplate
            open={open}
            width={560}
            handleClose={handleClose}
            ariaLabel="Exclude by pattern"
        >
            <div className="exclude-pattern">
                <h2 className="exclude-pattern__title">Exclude by pattern</h2>
                <p className="exclude-pattern__sub">
                    Pages whose address matches the pattern are excluded from this review. Only
                    included items are affected.
                </p>
                <div className="exclude-pattern__body">
                    <label htmlFor={`${id}-pattern`}>Pattern (regular expression)</label>
                    <input
                        id={`${id}-pattern`}
                        type="text"
                        placeholder="e.g. /blog/|/news/"
                        value={pattern}
                        onChange={evt => setPattern(evt.target.value)}
                        aria-describedby={`${id}-count`}
                    />
                    <span className="exclude-pattern__count" id={`${id}-count`} role="status">
                        {compiled
                            ? `Matches ${matches} of the ${totalLoaded} loaded item${totalLoaded === 1 ? "" : "s"}.`
                            : "The match count updates as you type."}
                    </span>
                    <div className="exclude-pattern__check">
                        <input
                            type="checkbox"
                            id={`${id}-persist`}
                            checked={persist}
                            onChange={evt => setPersist(evt.target.checked)}
                        />
                        <label htmlFor={`${id}-persist`}>
                            Also save to the crawl settings for future crawls
                        </label>
                    </div>
                    {showErrors && error && (
                        <div className="exclude-pattern__error" role="alert">
                            {error}
                        </div>
                    )}
                </div>
                <div className="exclude-pattern__actions">
                    <Button
                        className="thirt-button"
                        variant="text"
                        onClick={handleClose}
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="primary-button"
                        variant="text"
                        onClick={submit}
                        disabled={busy}
                    >
                        {busy ? "Working…" : "Exclude"}
                    </Button>
                </div>
            </div>
        </ModalTemplate>
    );
};

export default ExcludeByPatternDialog;
